import React from "react";
import { WidgetWidth } from "@/types/dashboard";
import { GripVertical, EyeOff, Maximize2, ArrowUp, ArrowDown, Move } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

interface WidgetWrapperProps {
  id: string;
  title: string;
  width: WidgetWidth;
  isEditMode: boolean;
  onRemove?: () => void;
  onToggleWidth?: () => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  isFirst?: boolean;
  isLast?: boolean;
  onDragStart?: (e: React.DragEvent) => void;
  onDragOver?: (e: React.DragEvent) => void;
  onDrop?: (e: React.DragEvent) => void;
  children: React.ReactNode;
}

export const WidgetWrapper: React.FC<WidgetWrapperProps> = ({
  id,
  title,
  width,
  isEditMode,
  onRemove,
  onToggleWidth,
  onMoveUp,
  onMoveDown,
  isFirst,
  isLast,
  onDragStart,
  onDragOver,
  onDrop,
  children,
}) => {
  // Responsive column span mapping
  const colSpanClass =
    width === "full"
      ? "col-span-1 sm:col-span-2 lg:col-span-3 xl:col-span-4"
      : width === "wide"
      ? "col-span-1 sm:col-span-2 lg:col-span-2 xl:col-span-3"
      : width === "medium"
      ? "col-span-1 sm:col-span-2 lg:col-span-2 xl:col-span-2"
      : "col-span-1 sm:col-span-1 lg:col-span-1 xl:col-span-1";

  if (!isEditMode) {
    return <div className={`relative transition-all duration-200 ${colSpanClass}`}>{children}</div>;
  }

  // Edit Mode Container
  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      className={`relative group rounded-2xl border-2 border-dashed border-primary/40 bg-primary/[0.02] p-1.5 transition-all duration-200 hover:border-primary/80 hover:shadow-lg ${colSpanClass}`}
    >
      {/* Top Edit Toolbar */}
      <div className="flex items-center justify-between bg-white px-3 py-1.5 rounded-xl border border-slate-200 shadow-sm mb-2 text-xs">
        <div className="flex items-center gap-1.5 cursor-grab active:cursor-grabbing text-slate-500 hover:text-slate-800">
          <GripVertical className="h-4 w-4 text-blue-600" />
          <span className="font-bold text-slate-800 truncate max-w-[140px] sm:max-w-[200px]">{title}</span>
          <Badge variant="outline" className="text-[10px] capitalize px-1 py-0 h-4 ml-1 bg-slate-50 text-slate-600 border-slate-200">
            {width}
          </Badge>
        </div>

        <div className="flex items-center gap-1">
          {/* Accessible Move Up / Down Buttons for Touch and Keyboard */}
          {onMoveUp && (
            <Button
              size="icon"
              variant="ghost"
              disabled={isFirst}
              onClick={onMoveUp}
              className="h-6 w-6 text-muted-foreground hover:text-foreground disabled:opacity-30"
              title="Move Earlier"
            >
              <ArrowUp className="h-3.5 w-3.5" />
            </Button>
          )}
          {onMoveDown && (
            <Button
              size="icon"
              variant="ghost"
              disabled={isLast}
              onClick={onMoveDown}
              className="h-6 w-6 text-muted-foreground hover:text-foreground disabled:opacity-30"
              title="Move Later"
            >
              <ArrowDown className="h-3.5 w-3.5" />
            </Button>
          )}

          {/* Cycle Width Button */}
          {onToggleWidth && (
            <Button
              size="icon"
              variant="ghost"
              onClick={onToggleWidth}
              className="h-6 w-6 text-muted-foreground hover:text-foreground"
              title="Resize Widget (Compact / Medium / Wide / Full)"
            >
              <Maximize2 className="h-3.5 w-3.5" />
            </Button>
          )}

          {/* Hide / Remove Widget */}
          {onRemove && (
            <Button
              size="icon"
              variant="ghost"
              onClick={onRemove}
              className="h-6 w-6 text-destructive hover:bg-destructive/10"
              title="Hide Widget"
            >
              <EyeOff className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </div>

      <div className="pointer-events-none opacity-90">{children}</div>
    </div>
  );
};
